// "Confirm it's you" in the owner console (owner, 2026-11-05: saving a plan sent the owner to the
// sign-in page for the authenticator code, and every change in the open editor was lost). A call the
// server answers `401 auth.stepup_required` (a sensitive action more than 10 minutes after the last
// code) now opens this popup over the page: the code from the authenticator app (or a recovery code)
// renews the session's second factor (POST /auth/mfa/totp or /recovery, the same check as sign-in),
// then the API client sends the same call again (api.ts setStepUpHandler). Closing it changes nothing.
// Calls made at the same time share one popup.
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { normalizeDigits } from "@ady/format";
import { t } from "@ady/i18n";
import { Button, Dialog, Field, Input } from "@ady/ui";
import { api, errorMessage, setStepUpHandler, StepUpCancelled } from "./api";

interface Waiter {
  resolve: () => void;
  reject: (err: unknown) => void;
}

/** Mounted once inside the signed-in shells: installs the handler and draws the popup. */
export function StepUpHost() {
  const waiters = useRef<Waiter[]>([]);
  const [open, setOpen] = useState(false);

  const settle = useCallback((ok: boolean) => {
    const list = waiters.current;
    waiters.current = [];
    setOpen(false);
    for (const w of list) {
      if (ok) w.resolve();
      else w.reject(new StepUpCancelled());
    }
  }, []);

  useEffect(() => {
    setStepUpHandler(
      () =>
        new Promise<void>((resolve, reject) => {
          waiters.current.push({ resolve, reject });
          setOpen(true);
        }),
    );
    return () => {
      setStepUpHandler(null);
      // Unmounting (sign-out) with a popup open: the waiting calls end as cancelled.
      const list = waiters.current;
      waiters.current = [];
      for (const w of list) w.reject(new StepUpCancelled());
    };
  }, []);

  return <StepUpDialog open={open} onDone={() => settle(true)} onCancel={() => settle(false)} />;
}

function StepUpDialog({
  open,
  onDone,
  onCancel,
}: {
  open: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) return;
    setRecovery(false);
    setCode("");
    setError(undefined);
  }, [open]);

  const confirm = async (e?: FormEvent) => {
    e?.preventDefault();
    if (busy) return;
    const value = recovery ? code.trim().toUpperCase() : code;
    const valid = recovery ? /^[A-Z0-9-]{10,16}$/.test(value) : /^\d{6}$/.test(value);
    if (!valid) return setError(recovery ? t("stepUp.recoveryInvalid") : t("stepUp.codeInvalid"));
    setError(undefined);
    setBusy(true);
    try {
      await (recovery ? api.recoveryCode(value) : api.totp(value));
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={t("stepUp.title")}
      description={t("console.mfaLead")}
      size="sm"
      data-testid="console-stepup"
      confirm={{
        label: t("stepUp.confirm"),
        loading: busy,
        loadingLabel: t("stepUp.confirming"),
        onClick: () => void confirm(),
        "data-testid": "console-stepup-confirm",
      }}
    >
      <form onSubmit={confirm} noValidate className="flex flex-col gap-3 pt-4">
        <Field
          label={recovery ? t("stepUp.recoveryLabel") : t("auth.mfa.totpLabel")}
          error={error}
          required
        >
          <Input
            key={recovery ? "recovery" : "totp"}
            value={code}
            inputMode={recovery ? "text" : "numeric"}
            autoComplete="one-time-code"
            autoCapitalize={recovery ? "characters" : "none"}
            spellCheck={false}
            maxLength={recovery ? 16 : 6}
            ledger
            onChange={(e) =>
              setCode(
                recovery
                  ? e.target.value
                  : normalizeDigits(e.target.value).replace(/\D/g, "").slice(0, 6),
              )
            }
            data-testid="console-stepup-code"
            autoFocus
          />
        </Field>
        <Button
          variant="ghost"
          onClick={() => {
            setRecovery(!recovery);
            setCode("");
            setError(undefined);
          }}
        >
          {recovery ? t("console.useTotp") : t("console.useRecovery")}
        </Button>
      </form>
    </Dialog>
  );
}
